import * as CookieConsent from 'vanilla-cookieconsent';

type ConsentChangeCallback = (acceptedCategories: string[]) => void;

const changeListeners: ConsentChangeCallback[] = [];

const ALL_CATEGORIES = ['necessary', 'analytics'];

function determineAction(accepted: string[]): 'accept_all' | 'reject_all' | 'custom' {
  if (accepted.length >= ALL_CATEGORIES.length && ALL_CATEGORIES.every((c) => accepted.includes(c))) {
    return 'accept_all';
  }
  if (accepted.length === 1 && accepted[0] === 'necessary') {
    return 'reject_all';
  }
  return 'custom';
}

function logConsentToServer(categories: string[], action: string): void {
  const authToken = (() => {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            return parsed?.access_token ?? null;
          }
        }
      }
    } catch { /* ignore */ }
    return null;
  })();

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  fetch('/api/privacy/consent', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      categories,
      policy_version: 1,
      action,
      gpc_enabled: detectGPC(),
    }),
  }).catch(() => {
    // Consent logging is best-effort; don't block the user experience
  });
}

function detectGPC(): boolean {
  return !!(navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
}

export const cookieConsentConfig: Parameters<typeof CookieConsent.run>[0] = {
  cookie: {
    name: 'st_cookie_consent',
    expiresAfterDays: 182,
  },

  revision: 1,

  guiOptions: {
    consentModal: {
      layout: 'bar inline',
      position: 'bottom',
      equalWeightButtons: true,
    },
    preferencesModal: {
      layout: 'box',
      position: 'right',
      equalWeightButtons: true,
    },
  },

  categories: {
    necessary: {
      enabled: true,
      readOnly: true,
    },
    analytics: {
      enabled: false,
      ...(detectGPC() ? { enabled: false } : {}),
    },
  },

  language: {
    default: 'en',
    translations: {
      en: {
        consentModal: {
          title: 'We use cookies',
          description:
            'We use essential cookies for core functionality and optional analytics cookies to improve your experience. You can learn more in our <a href="/privacy">Privacy Policy</a>.',
          acceptAllBtn: 'Accept All',
          acceptNecessaryBtn: 'Reject All',
          showPreferencesBtn: 'Manage Preferences',
        },
        preferencesModal: {
          title: 'Cookie Preferences',
          acceptAllBtn: 'Accept All',
          acceptNecessaryBtn: 'Reject All',
          savePreferencesBtn: 'Save Preferences',
          closeIconLabel: 'Close',
          sections: [
            {
              title: 'Cookie Usage',
              description:
                'We use cookies to ensure core functionality and to help us understand how you use StellarTrail so we can improve it. You can choose which categories to allow below.',
            },
            {
              title: 'Essential Cookies',
              description:
                'Required for authentication, theme preferences, coach history, and alert preferences. These cannot be disabled.',
              linkedCategory: 'necessary',
            },
            {
              title: 'Analytics Cookies',
              description:
                'Help us understand how you use StellarTrail through PostHog analytics and Sentry error reporting. This data helps us fix bugs and improve the product.',
              linkedCategory: 'analytics',
            },
            {
              title: 'More Information',
              description:
                'For more details about how we use cookies, please visit our <a href="/privacy">Privacy Policy</a>.',
            },
          ],
        },
      },
    },
  },

  onFirstConsent: ({ cookie }) => {
    const accepted = cookie.categories || [];
    // If GPC is active, ensure analytics is rejected
    if (detectGPC()) {
      // Analytics already defaults to disabled; listeners notified below
    }
    logConsentToServer(accepted, determineAction(accepted));
    changeListeners.forEach((cb) => cb(accepted));
  },

  onConsent: ({ cookie }) => {
    const accepted = cookie.categories || [];
    logConsentToServer(accepted, determineAction(accepted));
    changeListeners.forEach((cb) => cb(accepted));
  },

  onChange: ({ cookie }) => {
    const accepted = cookie.categories || [];
    logConsentToServer(accepted, 'change');
    changeListeners.forEach((cb) => cb(accepted));
  },
};

/**
 * Check if the user has consented to analytics cookies.
 */
export function hasAnalyticsConsent(): boolean {
  return CookieConsent.acceptedCategory('analytics');
}

/**
 * Open the cookie preferences modal so users can change their choices.
 */
export function showCookieSettings(): void {
  CookieConsent.showPreferences();
}

/**
 * Register a callback that fires whenever consent changes.
 */
export function onConsentChange(callback: ConsentChangeCallback): void {
  changeListeners.push(callback);
}

export { CookieConsent };
